/**
 * Converts a date string in YYYY-MM-DD format to an ISO string
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string|null} ISO date string or null if dateString is empty/null
 */
export const formatDateToISOString = (dateString) => {
  return dateString ? new Date(dateString + "T00:00:00Z").toISOString() : null;
};

/**
 * Converts a Date object or ISO date string to YYYY-MM-DD format
 * @param {Date|string} date - Date object or ISO date string
 * @returns {string} Date string in YYYY-MM-DD format or empty string if date is null/undefined
 */
export const formatDateToYYYYMMDD = (date) => {
  return date ? new Date(date).toISOString().split("T")[0] : "";
};

/**
 * Returns today's date as a string in YYYY-MM-DD format
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayYYYYMMDD = () => {
  return new Date().toISOString().split("T")[0];
};

/**
 * Returns true when the input is a valid Date instance.
 * @param {unknown} value - Value to validate
 * @returns {boolean} True when value is a Date with a valid timestamp
 */
export const isValidDate = (value) => {
  return value instanceof Date && !Number.isNaN(value.getTime());
};

/**
 * Safely parses a date-like value to a Date.
 * @param {Date|string|number|null|undefined} value - Date-like input
 * @returns {Date|null} Parsed Date or null if the input is invalid
 */
export const parseDateOrNull = (value) => {
  if (value == null || value === "") return null;

  const parsedDate = value instanceof Date ? new Date(value) : new Date(value);
  return isValidDate(parsedDate) ? parsedDate : null;
};

/**
 * Parses a date-like value and falls back when invalid.
 * @param {Date|string|number|null|undefined} value - Date-like input
 * @param {Date|string|number} fallback - Fallback date-like input
 * @returns {Date} Parsed Date or parsed fallback (or current time as last resort)
 */
export const parseDateOrFallback = (value, fallback = new Date()) => {
  const parsedDate = parseDateOrNull(value);
  if (parsedDate) return parsedDate;

  const parsedFallback = parseDateOrNull(fallback);
  return parsedFallback ?? new Date();
};

/**
 * Builds a safe, normalized [start, end] range even when inputs are missing,
 * invalid, or reversed.
 * @param {Date|string|number|null|undefined} startDate - Desired start
 * @param {Date|string|number|null|undefined} endDate - Desired end
 * @param {Date|string|number} fallbackDate - Fallback date-like input
 * @returns {{safeStartDate: Date, safeEndDate: Date, normalizedStartDate: Date, normalizedEndDate: Date}}
 */
export const getNormalizedDateRange = (
  startDate,
  endDate,
  fallbackDate = new Date(),
) => {
  const safeStartDate = parseDateOrFallback(startDate, fallbackDate);
  const safeEndDate = parseDateOrFallback(endDate, fallbackDate);

  if (safeStartDate <= safeEndDate) {
    return {
      safeStartDate,
      safeEndDate,
      normalizedStartDate: safeStartDate,
      normalizedEndDate: safeEndDate,
    };
  }

  return {
    safeStartDate,
    safeEndDate,
    normalizedStartDate: safeEndDate,
    normalizedEndDate: safeStartDate,
  };
};

